import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    fetchManifiestos,
    fetchManifiestoById,
    fetchManifiestosByUserProjects,
    createManifiesto,
    updateManifiesto,
    deleteManifiesto,
    fetchPasajeros,
    addPasajero,
    updatePasajero,
    deletePasajero,
    assignEmployee,
    assignVehicle,
    fetchHistorial,
    fetchRutasByManifiesto,
    assignRutaToManifiesto,
    removeEmployee,
    removeVehicle,
    getManifestPdf,
    getManifestPdfSF,
    getManifestRoadSheet10,
    getRoadMapPdf,
    sendMessageWhatsapp as sendMessageWhatsappApi,
    updateManifestState,
    searchManifestsApi,
    fetchManifestRouteTemplateApi,
    fetchManifiestosInternos,
    fetchManifiestosExternos,
    fetchManifiestosFiltered,
} from "../api/administrationService";
import {
    fetchReferenceManifestForBulk,
    createBulkManifestsFromOrders,
} from "../api/operations/manifestApi";

export const useManifiestos = () => {
    const queryClient = useQueryClient();
    // Obtener todos los manifiestos
    const {
        data: manifiestos = [],
        error,
        isLoading,
    } = useQuery({
        queryKey: ["manifests"],
        queryFn: fetchManifiestos,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    // Invalidar caché tras una mutación
    const invalidateManifiestosCache = () => {
        queryClient.invalidateQueries({ queryKey: ["manifests"] });
    };

    // Mutación para crear un manifiesto
    const createMutation = useMutation({
        mutationFn: createManifiesto,
        onSuccess: invalidateManifiestosCache,
    });

    // **Obtener un manifiesto por ID con `useQuery`, no `useMutation`**
    const useFetchManifiesto = (id) => {
        return useQuery({
            queryKey: ["manifests", id],
            queryFn: () => fetchManifiestoById(id),
            //enabled: false,
            enabled: !!id, // Solo ejecuta si hay un ID válido
            staleTime: 1000 * 60 * 5,
        });
    };

    // Mutación para actualizar un manifiesto
    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updateManifiesto(id, data),
        onSuccess: invalidateManifiestosCache,

    });

    const updateEstadoMutation = useMutation({
        mutationFn: ({ id, data }) => updateManifestState(id, data),
        onSuccess: (_, variables) => {
            invalidateManifiestosCache();
            queryClient.invalidateQueries({ queryKey: ["manifests", variables.id] });
        },
    });

    // Mutación para eliminar un manifiesto
    const deleteMutation = useMutation({
        mutationFn: deleteManifiesto,
        onSuccess: invalidateManifiestosCache,
    });

    const printManifestPDF = async (manifestId) => {
        if (!manifestId) return null;

        try {
            const blob = await getManifestPdf(manifestId);
            const pdfBlobUrl = URL.createObjectURL(blob); // genera una URL temporal para el blob
            return pdfBlobUrl;
        } catch (error) {
            console.error('Error al obtener el PDF del manifiesto:', error);
            return null;
        }
    };

    const printManifestPDFSF = async (manifestId) => {
        if (!manifestId) return null;

        try {
            const blob = await getManifestPdfSF(manifestId);
            const pdfBlobUrl = URL.createObjectURL(blob); // genera una URL temporal para el blob
            return pdfBlobUrl;
        } catch (error) {
            console.error('Error al obtener el PDF del manifiesto sin filtro:', error);
            return null;
        }
    };

    const printManifestRoadSheet10 = async (manifestId) => {
        if (!manifestId) return null;

        try {
            const blob = await getManifestRoadSheet10(manifestId);
            const pdfBlobUrl = URL.createObjectURL(blob); // genera una URL temporal para el blob
            return pdfBlobUrl;
        } catch (error) {
            console.error('Error al obtener el PDF del anexo 10:', error);
            return null;
        }
    };

    const printRoadMapPDF = async (manifestId) => {
        if (!manifestId) return null;

        try {
            const blob = await getRoadMapPdf(manifestId);
            const pdfBlobUrl = URL.createObjectURL(blob); // genera una URL temporal para el blob
            return pdfBlobUrl;
        } catch (error) {
            console.error('Error al obtener el PDF de la hoja de ruta:', error);
            return null;
        }
    }

    const sendMessageWhatsappMutation = useMutation({
        mutationFn: sendMessageWhatsappApi,
        onSuccess: (data, variables) => {
            invalidateManifiestosCache();
            queryClient.invalidateQueries({ queryKey: ["manifests", variables] });
            return data;
        },
        onError: (error) => {
            console.error('Error al enviar mensaje a los pasajeros (en hook):', error);
            throw error;
        },
    });

    return {
        manifiestos,
        error,
        isLoading,
        useFetchManifiesto,
        createMutation,
        updateMutation,
        deleteMutation,
        updateEstadoMutation,
        printManifestPDF,
        printManifestPDFSF,
        printManifestRoadSheet10,
        printRoadMapPDF,
        //sendMessageWhatsapp: sendMessageWhatsappMutation.mutate,
        sendMessageWhatsappMutation,
    };
};

const DEFAULT_LIMIT = 100;
const normalizeTipo = (tipo) => (tipo === "internos" ? "internos" : "externos");

const normalizeOptionalNumber = (value) => {
    if (value === undefined || value === null || value === "") {
        return undefined;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
};

export const useManifestsPaged = ({
    serProInt,
    tipoServicioId,
    tipoServicioCodigo,
    proyectoId,
    estados,
    limit = DEFAULT_LIMIT,
    offset = 0,
    enabled = true,
} = {}) => {
    const safeLimit = Number.isFinite(Number(limit))
        ? Math.max(Number(limit), 1)
        : DEFAULT_LIMIT;

    const safeOffset = Number.isFinite(Number(offset))
        ? Math.max(Number(offset), 0)
        : 0;

    const safeTipoServicioId = normalizeOptionalNumber(tipoServicioId);
    const safeProyectoId = normalizeOptionalNumber(proyectoId);

    const safeTipoServicioCodigo = tipoServicioCodigo
        ? String(tipoServicioCodigo).trim().toUpperCase()
        : undefined;

    const normalizedEstados = Array.isArray(estados)
        ? [...new Set(estados.map((estado) => estado.trim().toUpperCase()))].sort()
        : undefined;

    const safeSerProInt =
        typeof serProInt === "boolean"
            ? serProInt
            : undefined;

    return useQuery({
        queryKey: [
            "manifests",
            "paged",
            {
                serProInt: safeSerProInt,
                tipoServicioId: safeTipoServicioId,
                tipoServicioCodigo: safeTipoServicioCodigo,
                proyectoId: safeProyectoId,
                estados: normalizedEstados,
                limit: safeLimit,
                offset: safeOffset,
            },
        ],

        queryFn: () =>
            fetchManifiestosFiltered({
                serProInt: safeSerProInt,
                tipoServicioId: safeTipoServicioId,
                tipoServicioCodigo: safeTipoServicioCodigo,
                proyectoId: safeProyectoId,
                estados: normalizedEstados,
                limit: safeLimit,
                offset: safeOffset,
            }),

        enabled: Boolean(enabled),

        staleTime: 1000 * 60 * 2,

        placeholderData: (previousData) => previousData,

        refetchOnWindowFocus: false,
    });
};

export const useManifiestosPorUsuario = () => {
    return useQuery({
        queryKey: ["manifests", "by-user-projects"],
        queryFn: fetchManifiestosByUserProjects,
        staleTime: 1000 * 60 * 5,
    });
};

// Hook para gestionar pasajeros dentro de un manifiesto
export const usePasajeros = (manifiestoDetalleId, options = {}) => {
    const queryClient = useQueryClient();

    const {
        data: pasajeros = [],
        error,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ["pasajeros", manifiestoDetalleId],
        queryFn: () => fetchPasajeros(manifiestoDetalleId),
        enabled: !!manifiestoDetalleId,
        ...options,
    });

    const invalidatePasajerosCache = () => {
        queryClient.invalidateQueries({ queryKey: ["pasajeros", manifiestoDetalleId] });
    };

    const addMutation = useMutation({
        mutationFn: (data) => addPasajero(manifiestoDetalleId, data),
        onSuccess: invalidatePasajerosCache,
    });

    const updateMutation = useMutation({
        mutationFn: ({ manifiestoDetalleId, data }) => updatePasajero(manifiestoDetalleId, data),
        onSuccess: invalidatePasajerosCache,
    });

    const deleteMutation = useMutation({
        mutationFn: ({ manifiestoDetalleId, data }) =>
            deletePasajero(manifiestoDetalleId, data),
        onSuccess: invalidatePasajerosCache,
    });

    return {
        pasajeros,
        error,
        isLoading,
        refetch,
        addMutation,
        updateMutation,
        deleteMutation,
    };
};

// Hook para gestionar asignación de empleados y vehículos
export const useAsignaciones = (manifiestoId) => {
    const queryClient = useQueryClient();

    const assignEmployeeMutation = useMutation({
        mutationFn: (data) => assignEmployee(manifiestoId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["manifests", manifiestoId] });
        },
    });

    const removeEmployeeMutation = useMutation({
        mutationFn: (data) => removeEmployee(manifiestoId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["manifests", manifiestoId] });
        },
    });

    const assignVehicleMutation = useMutation({
        mutationFn: (data) => assignVehicle(manifiestoId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["manifests", manifiestoId] });
        },
    });

    const removeVehicleMutation = useMutation({
        mutationFn: (data) => removeVehicle(manifiestoId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["manifests", manifiestoId] });
        },
    });

    return {
        assignEmployeeMutation,
        removeEmployeeMutation,
        assignVehicleMutation,
        removeVehicleMutation,
    };
};

// Hook para obtener historial de un manifiesto
export const useHistorial = (manifiestoId) => {
    const { data: historial = [], error, isLoading } = useQuery({
        queryKey: ["historial", manifiestoId],
        queryFn: () => fetchHistorial(manifiestoId),
        enabled: !!manifiestoId,
    });

    return { historial, error, isLoading };
};

export const useRutasManifiesto = (manifiestoId) => {
    const queryClient = useQueryClient();

    // Consultar paradas actuales del manifiesto
    const {
        data: paradas = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ["manifiesto-paradas", manifiestoId],
        queryFn: () => fetchRutasByManifiesto(manifiestoId),
        enabled: !!manifiestoId,
    });
    
    const assignRuta =
        useMutation({
            mutationFn: ({ manifiestoId, payload,
            }) =>
                assignRutaToManifiesto(manifiestoId, payload),

            onSuccess: (_data, variables) => {
                const id = variables.manifiestoId;
                queryClient.invalidateQueries({ queryKey: ["manifiesto-paradas", id,], });
                queryClient.invalidateQueries({ queryKey: ["manifests", id,], });
                queryClient.invalidateQueries({ queryKey: ["manifests",], });
                queryClient.invalidateQueries({ queryKey: ["manifests", "paged",], });
            },
        });

    return {
        paradas,
        isLoading,
        error,
        assignRuta,
    };
};

export const useSearchManifests = ({ q, limit = 10, enabled }) => {
    return useQuery({
        queryKey: ["manifests-search", q, limit],
        queryFn: () => searchManifestsApi({ q, limit }),
        enabled: !!enabled,          // ahora depende 100% de enabled
        staleTime: 30_000,
        initialData: [],
    });
};

export const useManifestRouteTemplate = (originManifestId, enabled) => {
    return useQuery({
        queryKey: ["manifest-route-template", originManifestId],
        queryFn: () => fetchManifestRouteTemplateApi(originManifestId),
        enabled: enabled && !!originManifestId,
    });
};

export const useReferenceManifestForBulk = (manifestId, enabled = false) => {
    return useQuery({
        queryKey: ["manifests", "reference-bulk", manifestId],
        queryFn: () => fetchReferenceManifestForBulk(manifestId),
        enabled: Boolean(enabled && manifestId),
        staleTime: 1000 * 60,
        retry: false,
    });
};

export const useCreateBulkManifestsFromOrders = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createBulkManifestsFromOrders,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["manifests"] });
            queryClient.invalidateQueries({ queryKey: ["manifests", "paged"] });
            queryClient.invalidateQueries({ queryKey: ["manifests-search"] });
            queryClient.invalidateQueries({ queryKey: ["orders"] });
            queryClient.invalidateQueries({ queryKey: ["service-orders"] });
        },
        retry: false,
    });
};
